import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Icon, Input, Loader, Message, Table } from 'semantic-ui-react';
import { api } from '../api/apiClient';
import PaginationFooter from './PaginationFooter';
import { useTablePreferences } from '../hooks/useTablePreferences';
import { formatTimestamp } from '../utils/timezone.utils';

const OPERATING_SYSTEM_LABELS = {
    Windows: 'Windows',
    LinuxDebian: 'Linux (Debian)',
    LinuxRPM: 'Linux (RPM)',
    Mac: 'Mac'
};

const copyToClipboard = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        const copied = document.execCommand('copy');
        document.body.removeChild(textarea);
        return copied;
    }
};

const headerCellStyle = { background: '#f8fafc', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' };

const AgentCreationHistory = () => {
    const [requests, setRequests] = useState([]);
    const [scope, setScope] = useState('own');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [copiedId, setCopiedId] = useState(null);
    const { rowsPerPage, tableDensity, activePage, setActivePage } = useTablePreferences();

    const loadRequests = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await api.unifiedAgent.listRequests();
            setRequests(Array.isArray(response?.data?.data) ? response.data.data : []);
            setScope(response?.data?.scope || 'own');
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || 'Failed to load agent creation history');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRequests();
    }, [loadRequests]);

    const showRequester = scope === 'all';

    const handleCopyCloudName = async (request) => {
        if (await copyToClipboard(request.cloudName)) {
            setCopiedId(request.id);
            setTimeout(() => setCopiedId((current) => (current === request.id ? null : current)), 1000);
        }
    };

    const filteredRequests = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return requests;
        return requests.filter((request) => (
            [request.cloudName, request.requesterEmail, request.agentVersion, request.environment, request.suffix]
                .some((value) => (value || '').toLowerCase().includes(term))
        ));
    }, [requests, search]);

    useEffect(() => {
        setActivePage(1);
    }, [search, requests, setActivePage]);

    const totalPages = Math.max(1, Math.ceil(filteredRequests.length / Math.max(1, rowsPerPage)));
    const pageStart = (activePage - 1) * rowsPerPage;
    const pageEnd = pageStart + rowsPerPage;
    const visibleRequests = filteredRequests.slice(pageStart, pageEnd);

    return (
        <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center', marginBottom: '1rem' }}>
                <Input
                    icon="search"
                    placeholder={showRequester ? 'Search cloud, requester, version...' : 'Search cloud, version...'}
                    value={search}
                    onChange={(e, { value }) => setSearch(value)}
                    style={{ flex: '1 1 260px', maxWidth: '420px' }}
                />
                <Button basic icon labelPosition="left" onClick={loadRequests} disabled={loading} style={{ marginLeft: 'auto' }}>
                    <Icon name="refresh" />
                    Refresh
                </Button>
            </div>

            {error && (
                <Message negative onDismiss={() => setError(null)}>
                    <Message.Header>Could not load history</Message.Header>
                    <p>{error}</p>
                </Message>
            )}

            {loading ? (
                <Loader active inline="centered" content="Loading history..." style={{ margin: '2rem auto' }} />
            ) : filteredRequests.length === 0 ? (
                <Message info>
                    <p>{requests.length === 0 ? 'No agent creation requests yet.' : 'No requests match your search.'}</p>
                </Message>
            ) : (
                <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <Table
                        unstackable
                        compact={tableDensity === 'compact'}
                        size={tableDensity === 'compact' ? 'small' : undefined}
                        style={{ margin: 0, border: 'none' }}
                    >
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell style={headerCellStyle}>Timestamp</Table.HeaderCell>
                                <Table.HeaderCell style={headerCellStyle}>Cloud Name</Table.HeaderCell>
                                <Table.HeaderCell style={headerCellStyle}>Operating System</Table.HeaderCell>
                                <Table.HeaderCell style={headerCellStyle}>Backup Type</Table.HeaderCell>
                                <Table.HeaderCell style={headerCellStyle}>POD</Table.HeaderCell>
                                <Table.HeaderCell style={headerCellStyle}>Agent Version</Table.HeaderCell>
                                <Table.HeaderCell style={headerCellStyle}>Suffix</Table.HeaderCell>
                                <Table.HeaderCell style={headerCellStyle}>Submitted By</Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {visibleRequests.map((request, index) => (
                                <Table.Row key={request.id} style={{ background: index % 2 === 0 ? '#fff' : '#f8fafc' }}>
                                    <Table.Cell style={{ whiteSpace: 'nowrap' }}>{formatTimestamp(request.createdAt)}</Table.Cell>
                                    <Table.Cell style={{ fontWeight: 600, color: '#062D4D', whiteSpace: 'nowrap' }}>
                                        {request.cloudName}
                                        <Icon
                                            name={copiedId === request.id ? 'check' : 'copy'}
                                            onClick={() => handleCopyCloudName(request)}
                                            title="Copy cloud name"
                                            aria-label="Copy cloud name"
                                            style={{
                                                marginLeft: '0.4rem',
                                                cursor: 'pointer',
                                                color: copiedId === request.id ? '#22c55e' : '#9ca3af',
                                                transition: 'color 120ms ease-out'
                                            }}
                                        />
                                    </Table.Cell>
                                    <Table.Cell>{OPERATING_SYSTEM_LABELS[request.operatingSystem] || request.operatingSystem}</Table.Cell>
                                    <Table.Cell>{request.backupType}</Table.Cell>
                                    <Table.Cell>{request.environment}</Table.Cell>
                                    <Table.Cell>{request.agentVersion}</Table.Cell>
                                    <Table.Cell>{request.suffix || '-'}</Table.Cell>
                                    <Table.Cell>{request.requesterEmail}</Table.Cell>
                                </Table.Row>
                            ))}
                        </Table.Body>
                    </Table>
                </div>
                <PaginationFooter
                    activePage={activePage}
                    totalPages={totalPages}
                    totalItems={requests.length}
                    filteredItems={filteredRequests}
                    hasActiveFilters={Boolean(search.trim())}
                    onPageChange={setActivePage}
                    pageStart={pageStart}
                    pageEnd={pageEnd}
                />
                </div>
            )}
        </div>
    );
};

export default AgentCreationHistory;
